/* ============================================================
   JUCE 3.0 · theme-shelf.js - the collection filter rail and the shelf head.

   Only a page with [data-cfilter] (sections/main-collection.liquid) uses
   either, so the home page and every product page no longer carry them.
   theme.js imports this file beside the engine (bootEngineSurfaces), and the
   layout modulepreloads it on the templates that render the grid.

   HOW IT REACHES THEME.JS. The code below is theme.js's own, moved as it
   stood. What it still uses from theme.js arrives through mount(): the
   `let` list is filled once, from the kit theme.js hands over (chunkKit
   there), before either controller runs.
   Nothing here imports theme.js by URL: a URL without the ?v= digest
   would be a second copy of theme.js, booted twice.

   JE is read at mount, and theme.js mounts this file only after loadEngine()
   has settled, so it is the engine or null exactly as it was in theme.js.
   ============================================================ */
let CF_VIEW_KEY,
  JE,
  onResizeSettled,
  setMoney,
  srchMoney,
  srchNorm,
  syncInRoom;

export function mount(k) {
  ({
    CF_VIEW_KEY,
    JE,
    onResizeSettled,
    setMoney,
    srchMoney,
    srchNorm,
    syncInRoom
  } = k);
  return { initCollectionFilter, initShelfHead };
}

/* ---- Collection filter: the faceted shop. A left rail (smart search +
   preference / season / occasion / family facets + the Top·Heart·Base note
   pyramid + a price range) filters the poster grid entirely client-side and
   mirrors every choice into the URL, so any view is shareable and Back just
   leaves the shop. It runs the same engine as the popup (JuceEngine.search /
   layerPairs) so the search reads intent ("fresh summer for her") and, when
   a scent is clearly in play, a "layers well with" rail appears. Old
   ?type= / ?notes= / ?season= deep links still resolve. No-ops off its DOM. */
/* ADDING A SIXTH FAMILY: this array and the rail's markup are the whole job.
   LOOM no longer keeps a copy of this list - juce-loom.js reads the family
   names off the [data-cf-group] / [data-facet] attributes in
   sections/main-collection.liquid, which is the same string used here - so a
   new facet is recorded in the journey by default. The one way to break that
   is to give a family a state key that is NOT its data-facet value. */
const CF_FACETS = ['preference', 'size', 'season', 'occasion', 'family', 'notes'];
/* OR within these. SIZE IS ONE OF THEM (owner, 2026-08-31: "Filter option to
   select 10ml and 50ml"), and it has to be: with two chips and AND between
   them, pressing both would ask for a scent that is a 50ml and a 10ml at the
   same time and empty the shelf, which is the opposite of what pressing both
   looks like it means. OR reads the way the control looks - "either of these
   is fine" - and pressing both is the same as pressing neither, which is the
   honest answer for a catalogue where almost everything comes in both. */
const CF_MULTI = { size: 1, season: 1, occasion: 1, notes: 1 };
/* At or below this many results the end-of-wall panel stops introducing the
   house and starts offering a rescue instead. Four is a row on a phone: at
   five the shopper still has something to look at, at four the screen is
   mostly empty and the refinement has clearly overshot this shelf. */
const CF_NARROW = 4;
function cfKey(s) { return srchNorm(s).replace(/\s+/g, '-'); }
function cfMoney(cents) { return srchMoney(cents); }

function initCollectionFilter() {
  const root = document.querySelector('[data-cfilter]');
  const grid = root && root.querySelector('[data-filter-grid]');
  const idxNode = document.querySelector('[data-cfilter-index]');   /* the index sits just outside the rail */
  if (!root || !grid || !idxNode || !JE) return;

  let raw;
  try { raw = JSON.parse(idxNode.textContent); } catch (e) { return; }
  const PREP = JE.prepare(raw.p || [], raw.notes || []);
  if (!PREP.list.length) return;

  const qs = (s) => root.querySelector(s);
  const qsa = (s) => Array.from(root.querySelectorAll(s));
  /* ---- LOOKUPS THAT HAPPEN AGAIN LATER ----
     qsa() is right for the collecting done once at init, when everything this
     section owns is inside it. It is wrong for anything re-read on every apply:
     past the fold the rail is moved into the site header (initShelfHead), out
     of .cx, and a root-scoped re-read then finds none of the chips it is meant
     to be painting - the panel would open with every facet showing unpressed
     and every count blank. Deduped, because for most of a page's life the rail
     IS inside root and would otherwise answer twice.

     `rail` is declared further down; every live() call site runs from apply(),
     and the first apply() is the last line of this function. */
  const live = (s) => Array.from(new Set([
    ...root.querySelectorAll(s),
    ...(rail ? rail.querySelectorAll(s) : []),
  ]));

  /* ---- THE MONEY THIS SHELF REASONS IN ----
     `pc` on an index row is the 50ml price, and it means that everywhere -
     the search popup, the layering rail, the discovery builder all depend on
     it. A shelf cut by SIZE sells in a different number, and until 2026-09-04
     nothing carried that number across: /pages/minis built its price bands off
     the 10ml (main-collection §1) and then tested them against the 50ml on the
     row, so every one of the three bands emptied the grid. main-collection now
     hands over its own answer as `pk`, handle -> cents, and only when the two
     differ; the row is left alone. Four things read fk[h].pc - the band test,
     the band counts and the two price sorts - and all four want the shelf's
     price, which is also what makes "sort by price" honest on a cut shelf
     rather than accidentally right because the tiers happen to be
     proportional. */
  const PK = (raw && raw.pk) || null;

  /* per-product facet keys + the original (featured) order */
  const fk = {}, order = {};
  PREP.list.forEach((rec, i) => {
    order[rec.h] = i;
    fk[rec.h] = {
      preference: cfKey(rec.raw.g || ''),
      family: cfKey(rec.raw.c || ''),
      /* The rungs this scent is genuinely sold in - snippets/search-row walks
         the variants for them under the same sellable rule the cards use, so a
         retired 10ml is not offered here any more than it is on a card. A row
         from a session that cached the index before `sz` existed has none, and
         an empty set simply never matches a size chip rather than throwing. */
      size: new Set((rec.raw.sz || []).map(cfKey)),
      season: new Set((rec.raw.se || []).map(cfKey)),
      occasion: new Set((rec.raw.ti || []).map(cfKey)),
      notes: new Set(rec.notes.map(cfKey)),
      pc: (PK && PK[rec.h] > 0) ? PK[rec.h] : rec.pc, t: rec.t,
    };
  });
  const cards = {};
  qsa('[data-card]').forEach((c) => { if (c.dataset.handle) cards[c.dataset.handle] = c; });

  /* label lookup for the active-pill summary (facet/val -> human label) */
  const labels = {};
  qsa('[data-facet][data-val]').forEach((c) => { labels[c.dataset.facet + '/' + c.dataset.val] = c.dataset.label || c.querySelector('.cf-chip__label').textContent; });

  const state = { preference: new Set(), size: new Set(), season: new Set(), occasion: new Set(), family: new Set(), notes: new Set(), pmin: null, pmax: null, q: '', sort: 'best' };

  /* ---- DOM hooks ---- */
  const searchIn = qs('[data-cf-search]');
  const searchClear = qs('[data-cf-search-clear]');
  const readOut = qs('[data-cf-read]');
  const sortSel = qs('[data-cf-sort]');
  /* THE SHELF'S OWN OPENING SORT, read off the markup before hydrate can move
     it. It is 'best' everywhere except /collections/top-rated, which Liquid
     renders with the Top rated option `selected` because the wall itself is
     painted in rating order there (main-collection §2c). Two things hang off
     knowing it: the starting state below, and urlParams - a default must not
     be written into the address bar, or every visit to Top Rated would rewrite
     itself to ?sort=rated and the shopper would share a link that pins a sort
     they never chose.

     ONE CONSEQUENCE, ON PURPOSE: urlParams feeds the carry links too, so
     leaving Top Rated by "See all 41 scents" carries the refinement and NOT the
     sort, and the whole house opens in the house's own order. That is the same
     rule every shelf follows - each one opens in its own default and a sort
     travels only once the shopper has actually chosen one. */
  const sortOpen = (sortSel && sortSel.value) || 'best';
  /* `featured` was this option's value until 2026-08-26, when it was renamed to
     say what the order actually is (see main-collection). urlParams never wrote
     it - it was the default - so this can only turn up in a hand-made link, and
     one line is cheaper than leaving such a link to land on a sort that silently
     does not exist. */
  const sortAlias = (v) => (v === 'featured' ? 'best' : v);
  const activeBoxes = qsa('[data-cfilter-active]');
  const countOut = document.querySelectorAll('[data-coll-count]');
  /* the plural "s", in its own span so a one-hit grid does not read
     "1 scents" in the head, the bar and the sheet button all at once */
  const pluralOut = document.querySelectorAll('[data-coll-plural]');
  const sheetCount = qs('[data-coll-count-sheet]');
  /* DOCUMENT-scoped, not qs(): the filter control now also lives in the island,
     which is rendered outside <main> and therefore outside this section's root.
     Both badges mirror the same count. */
  const activeBadges = Array.from(document.querySelectorAll('[data-cf-active-n]'));
  const clearBtns = qsa('[data-cfilter-clear]');
  /* the way out of a narrow shelf - absent on the full catalogue, where
     there is nothing further to offer */
  const ofBox = qs('[data-cf-of]');
  const moreBox = qs('[data-cf-more]');
  const moreH = qs('[data-cf-more-h]');
  const moreC = qs('[data-cf-more-c]');
  /* Base href is read off the markup, once: the collection route is Liquid's
     to know (routes.all_products_collection_url), never this file's. */
  const carryLinks = qsa('[data-cf-carry]').map((a) => ({ a: a, base: a.getAttribute('href') || '' }));
  const noResult = qs('[data-cfilter-noresult]');
  const priceWrap = qs('[data-cf-price]');
  const bandChips = qsa('[data-cf-band]');
  /* Price is three tap-friendly bands, not a two-thumb slider. The slider's
     five hooks ([data-cf-range-lo/-hi/-fill/-out-lo/-out-hi]) are emitted by
     nothing in the theme any more, so the code that read them is gone with
     them. bMin/bMax below are still real: the bands compare against them. */
  let bMin = priceWrap ? parseInt(priceWrap.dataset.min, 10) : 0;
  let bMax = priceWrap ? parseInt(priceWrap.dataset.max, 10) : 0;
  /* The bands as the server drew them, so clearing the size chips can put them
     back. NOT recomputed from them: this shelf's own edges are Liquid's answer
     and the only place that arithmetic has to be trusted. */
  const bOpenMin = bMin, bOpenMax = bMax;
  const bandOpen = bandChips.map((c) => ({
    lo: c.dataset.lo, hi: c.dataset.hi, off: !!c.hidden,
    t: (c.querySelector('.cf-chip__label') || {}).textContent || '',
  }));

  /* ==================== THE WALL WEARS THE SIZE ====================
     (owner, 2026-09-04: "when you filter by size 10ml it still shows the
     50mls ... on filtering it must just basically change all the swatches".)

     The size chips narrowed the grid to the scents sold in that rung and then
     left every card resting on its 50ml bottle at its 50ml price - a filter
     answering a question nobody asked. A shopper who picks 10ml is not asking
     which scents happen to come in one; they are asking to see the shop in
     that size. So ONE size selected re-points every card at that rung, and
     clearing it puts the wall back the way the server drew it. Two chips
     pressed together are not one size, so that restores it too, which is the
     same answer OR already gives the grid.

     IT PRESSES THE CARD'S OWN BUTTON rather than repainting the card here.
     Every rung is already on the card as a [data-size-opt] carrying its
     photograph, its scene, its price, its compare-at and its variant id
     (snippets/size-options.liquid), and three listeners already answer a press
     on one: initSizes paints the price, the posted id and the link the poster
     carries, initMiniSwap swaps the poster and the reveal, initDockCommerce
     re-reads the island. Pressing the button IS the implementation, and there
     is no second description of what a size change means to fall out of step
     with the one a shopper's own tap uses.

     TWO THINGS HAVE TO BE TOLD IT IS NOT A SHOPPER, both narrow, both here.
     LOOM would file forty CTA presses on "10ml" for one press on a chip (its
     heat branch is already safe - a dispatched click carries no coordinates,
     and it drops those), and the desktop facet menu closes on any click
     outside itself, which would snap the panel shut under the shopper at the
     instant they chose a size. */
  const SZP = (raw && raw.szp) || null;
  const SZB = (raw && raw.szb) || null;

  /* The rung each card was rendered on, read before anything can move it. A
     one-size product has no [data-size-opt] at all and never appears here. */
  const openRung = {};
  Object.keys(cards).forEach((h) => {
    const on = cards[h].querySelector('[data-size-opt].is-active');
    if (on) openRung[h] = on;
  });
  /* cfKey('Bundle') is 'bundle' and cfKey('50ml') is '50ml' - which is exactly
     what the chips carry and what `sz` reports - so a rung is found by the word
     printed on it, and there is no list of size names in here to drift away
     from the store. */
  function rungFor(h, key) {
    const card = cards[h];
    if (!card || !key) return openRung[h] || null;
    const opts = card.querySelectorAll('[data-size-opt]');
    for (let i = 0; i < opts.length; i++) {
      if (cfKey((opts[i].textContent || '').trim()) === key) return opts[i];
    }
    return openRung[h] || null;
  }
  let wearing;
  let sizing = false;              /* read by the menu-close listener below */
  function wearSize(key) {
    if (key === wearing) return;
    wearing = key;
    sizing = true;
    /* LOOM's own seam for "this is not the shop" (assets/juce-loom.js). Its
       click listener is capture-phase and synchronous, and so is dispatching a
       click, so the attribute is up for exactly the presses it covers. */
    grid.setAttribute('data-loom-skip', '');
    try {
      /* EVERY card, not only the visible ones: a scent hidden by another facet
         has to already be wearing the right size for the moment that facet is
         cleared. A hidden card costs almost nothing - placeSizeThumb measures
         zero and returns before it can force a layout. */
      Object.keys(cards).forEach((h) => {
        const opt = rungFor(h, key);
        if (opt && opt.getAttribute('aria-pressed') !== 'true') opt.click();
      });
    } finally {
      grid.removeAttribute('data-loom-skip');
      sizing = false;
    }
  }

  /* ---- AND THE MONEY MOVES WITH IT ----
     The same lesson as `pk` above, one rung along: four things read fk[h].pc -
     the band test, the band counts and the two price sorts - and all four want
     the price the wall is actually showing. Without this, ticking 10ml leaves
     a rail offering "Over R 750" above a grid where nothing costs more than
     R 335, and pressing it answers in a currency the shopper cannot see.

     `szp` is each product's price in each rung and `szb` the band edges each
     rung would draw, both worked out by main-collection §1 - so the arithmetic
     behind a band lives in one file rather than in two that can disagree. */
  function shownSize() { return state.size.size === 1 ? Array.from(state.size)[0] : null; }
  let moneyIn;
  function setMoney(key, keep) {
    if (key === moneyIn) return;
    moneyIn = key;
    PREP.list.forEach((rec) => {
      const p = (key && SZP && SZP[rec.h] && SZP[rec.h][key]) || 0;
      fk[rec.h].pc = p > 0 ? p : ((PK && PK[rec.h] > 0) ? PK[rec.h] : rec.pc);
    });
    paintBands((key && SZB && SZB[key]) || null);
    /* A band chosen in 50ml money is not a band in 10ml money - the numbers
       under it have changed meaning, so the choice goes with them. Kept on the
       first pass only, which is a shared link arriving carrying both. */
    if (!keep) { state.pmin = null; state.pmax = null; }
  }
  function paintBands(b) {
    if (!bandChips.length) return;
    const rand = (c) => 'R ' + Math.round(c / 100);
    /* Edges land on R50, so a whole rand is the whole number - the same thing
       Liquid's `divided_by: 100` prints on the server-rendered chips. */
    const set = b
      ? [[b[0], b[1], 'Under ' + rand(b[1]), b[1] > b[0] && b[1] < b[3]],
         [b[1], b[2], rand(b[1]) + ' - ' + rand(b[2]), b[2] > b[1] && b[1] >= b[0] && b[2] <= b[3]],
         [b[2], b[3], 'Over ' + rand(b[2]), b[2] > b[0] && b[2] < b[3]]]
      : bandOpen.map((o) => [o.lo, o.hi, o.t, !o.off]);
    bMin = b ? b[0] : bOpenMin;
    bMax = b ? b[3] : bOpenMax;
    bandChips.forEach((chip, i) => {
      const s = set[i];
      if (!s) return;
      chip.dataset.lo = String(s[0]);
      chip.dataset.hi = String(s[1]);
      const l = chip.querySelector('.cf-chip__label');
      if (l) l.textContent = s[2];
      chip.hidden = !s[3];
    });
    /* The group carries the bounds too - it is where they were read from, and
       leaving a stale pair there would mislead the next reader of the DOM. */
    if (priceWrap) { priceWrap.dataset.min = String(bMin); priceWrap.dataset.max = String(bMax); }
  }

  /* ---- matching ---- */
  function passFacet(h, f, skip) {
    if (f === skip) return true;
    const sel = state[f]; if (!sel.size) return true;
    const v = fk[h][f];
    if (CF_MULTI[f]) { for (const x of sel) if (v.has(x)) return true; return false; }
    return sel.has(v);
  }
  function passPrice(h, skip) {
    if (skip === 'price') return true;
    const pc = fk[h].pc;
    if (state.pmin != null && pc < state.pmin) return false;
    if (state.pmax != null && pc > state.pmax) return false;
    return true;
  }
  function pass(h, skip) { return CF_FACETS.every((f) => passFacet(h, f, skip)) && passPrice(h, skip); }

  /* ---- ordering ---- */
  /* `order` is the source order of the client index, which is the collection's
     own order - best-selling on every shelf this store has. So the fall-through
     IS the "Best sellers" option, and it is also every other option's last
     tiebreak, which keeps a re-sort from shuffling equal rows about.

     'rated' (owner, 2026-08-26: "also add top rated"): score descending, then
     REVIEW COUNT descending. The tiebreak is a deliberate choice and not a
     stable-sort accident - a 4.9 from 9 reviews and a 4.9 from 103 are not
     equally trustworthy, and the shelf should lead with the one more people
     stood behind. There is NO Bayesian prior and no shrinkage here: nothing in
     this comparator invents a number the store did not publish, and if one is
     ever added it must be said out loud, in this comment, because a weighted
     score no longer matches the figure printed on the card.

     `|| 0` ON BOTH SIDES, and it is not belt-and-braces. prepare() coerces rt
     and rc to numbers, so a record that comes through the engine always has
     them - but `undefined - undefined` is NaN, NaN is falsy, and a NaN first
     term falls straight through to the tiebreak and then to source order. An
     unrated row would not sink; it would stay exactly where it started, which
     looks like a sort that quietly did nothing. Measured against a fixture with
     one unrated row before this was added. Zero sinks. */
  function cmp(rank) {
    if (rank) return (a, b) => rank[a.h] - rank[b.h];
    const s = state.sort;
    if (s === 'price-asc') return (a, b) => a.pc - b.pc || order[a.h] - order[b.h];
    if (s === 'price-desc') return (a, b) => b.pc - a.pc || order[a.h] - order[b.h];
    if (s === 'az') return (a, b) => a.t.localeCompare(b.t);
    if (s === 'rated') return (a, b) => (b.rt || 0) - (a.rt || 0) || (b.rc || 0) - (a.rc || 0) || order[a.h] - order[b.h];
    return (a, b) => order[a.h] - order[b.h];
  }

  /* ---- tiny builders ---- */
  function el(tag, cls, text) { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; }

  /* ---- render pass ---- */
  function apply(writeUrl) {
    /* FIRST, because everything below it - the price test, the band counts,
       both price sorts - reasons in whatever money this leaves behind. */
    setMoney(shownSize());
    const q = state.q.trim();
    let res = null, rank = null, searchSet = null;
    if (q) {
      res = JE.search(PREP, q, { limit: 9999 });
      searchSet = new Set(res.results.map((x) => x.p.h));
      rank = {}; res.results.forEach((x, i) => { rank[x.p.h] = i; });
    }
    /* visibility */
    const visible = [];
    PREP.list.forEach((rec) => {
      const ok = pass(rec.h) && (!q || searchSet.has(rec.h));
      const card = cards[rec.h];
      if (card) card.hidden = !ok;
      if (ok) visible.push(rec);
    });
    visible.sort(cmp(rank));
    /* reorder DOM: visible cards, in order, to the front */
    const frag = document.createDocumentFragment();
    visible.forEach((rec) => { const c = cards[rec.h]; if (c) frag.appendChild(c); });
    grid.appendChild(frag);
    /* After the grid has settled, so the cards this pass hid are already hidden
       and cost nothing to re-point. */
    wearSize(shownSize());

    /* counts */
    const n = visible.length;
    countOut.forEach((c) => { c.textContent = String(n); });
    pluralOut.forEach((c) => { c.hidden = n === 1; });
    if (sheetCount) sheetCount.textContent = String(n);
    if (noResult) noResult.hidden = n !== 0;
    grid.hidden = n === 0;
    wayOut(n);

    /* per-option counts + disabled state */
    live('[data-facet][data-val]').forEach((chip) => {
      const f = chip.dataset.facet, v = chip.dataset.val;
      let c = 0;
      for (const rec of PREP.list) {
        if (!pass(rec.h, f)) continue;
        if (q && !searchSet.has(rec.h)) continue;
        const kv = fk[rec.h][f];
        if (CF_MULTI[f] ? kv.has(v) : kv === v) c++;
      }
      const badge = chip.querySelector('[data-cf-count]');
      if (badge) badge.textContent = c ? String(c) : '';
      const on = state[f].has(v);
      chip.classList.toggle('is-empty', c === 0 && !on);
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    /* price bands: active state + per-band counts (exclude the price facet) */
    bandChips.forEach((band) => {
      const lo = parseInt(band.dataset.lo, 10), hi = parseInt(band.dataset.hi, 10);
      const effLo = state.pmin != null ? state.pmin : bMin;
      const effHi = state.pmax != null ? state.pmax : bMax;
      const bLo = lo <= bMin ? bMin : lo, bHi = hi >= bMax ? bMax : hi;
      const on = (state.pmin != null || state.pmax != null) && effLo === bLo && effHi === bHi;
      let c = 0;
      for (const rec of PREP.list) {
        if (!pass(rec.h, 'price')) continue;
        if (q && searchSet && !searchSet.has(rec.h)) continue;
        const pc = fk[rec.h].pc;
        if (pc >= lo && pc <= hi) c++;
      }
      band.setAttribute('aria-pressed', on ? 'true' : 'false');
      const badge = band.querySelector('[data-cf-count]');
      if (badge) badge.textContent = c ? String(c) : '';
      band.classList.toggle('is-empty', c === 0 && !on);
    });

    reflectActive();
    paintVibe();
    paintNotes();
    if (readOut) {
      /* the same "we read you" echo the popup shows, from the same parser */
      const line = q && res ? res.read.join(' · ') : '';
      readOut.hidden = !line; readOut.textContent = line ? 'Reading: ' + line : '';
    }
    if (searchClear) searchClear.hidden = !q;
    if (writeUrl !== false) syncUrl();
  }

  /* ---- What is on ----
     ONE description of the live refinement, painted into every box that asks
     for it: the row above the grid and the strip inside the rail. That second
     box is why this is a loop - on a phone the sheet covers the grid, so a
     shopper three groups into the note pyramid could not see, let alone drop,
     the season they set a minute earlier without closing the sheet to look.
     A DOM node lives in one place, so each box gets pills of its own.

     The search term is a pill like any other. It is a refinement, it is the
     one most likely to be lifted first, and outside the rail it had no way
     off at all. */
  /* THE SHELF ITSELF IS THE FIRST PILL (owner, 2026-08-28: "on filter show
     vibe options as being selected so that I can easily get out of a
     collection"). Standing in a vibe is a refinement like any other, and it
     was the only one with no way off. main-collection.liquid stamps the three
     data-cf-collection-* attributes only when the page IS a real collection -
     never on all, never on minis - so the absence of a label is the whole
     "should this pill exist" test.

     It leads the row because it is the widest thing on: it is holding
     twenty-seven scents out, and every pill after it is narrowing what is
     left. Marked `remove="collection"` so the handler below can tell it from a
     facet - dropping it is a NAVIGATION, not a state change. */
  const collLabel = root.dataset.cfCollectionLabel || '';
  const collOut = root.dataset.cfCollectionOut || '/collections/all';

  /* ---- THE BAND WEARS THE VIBE THE SHELF IS SHOWING ----
     (owner, 2026-08-28: "when viewing a vibe category the top section where it
     shows the vibe must have that bg, if you clear the category and stuff like
     that the image must go away and no longer in that category".)

     The second half of that sentence is the whole reason this lives here and
     not only in Liquid. A banner painted once at page load is a picture of the
     shelf the URL asked for, and the shopper can filter that shelf out from
     under it - the page would go on showing Midnight Allure over nine Fresh
     scents. So the band is recomputed on every apply() from one rule:

       the page IS a vibe collection      -> its own art (server-rendered, and
                                             it cannot change: dropping the
                                             shelf pill is a navigation)
       exactly ONE note family is picked  -> that family's vibe
       anything else                      -> no band

     The middle case is not decoration. Since 2026-08-27 each vibe IS a note
     family, so Ambery on /collections/all is Midnight Allure by another door,
     and unpicking it takes the painting off live. Two families at once is not
     a vibe and gets nothing - the shelf is no longer one room.

     ART comes from snippets/vibe-art.liquid as JSON keyed by the same
     handleized family the chips carry, so there is no second vocabulary to
     drift and no filename list in here to fall out of step with assets/. */
  const head = document.querySelector('[data-coll-head]');
  const pageVibe = head && head.classList.contains('coll-head--vibe');
  let ART = null;
  try {
    const n = root.querySelector('[data-vibe-art]');
    if (n) ART = JSON.parse(n.textContent);
  } catch (e) { ART = null; }

  function paintVibe() {
    if (!head || pageVibe || !ART) return;
    const fams = Array.from(state.family);
    const v = fams.length === 1 ? ART[fams[0]] : null;
    if (v) {
      head.style.setProperty('--vibe-wide', 'url(' + v.w + ')');
      head.style.setProperty('--vibe-tall', 'url(' + v.t + ')');
      head.classList.add('coll-head--vibe');
    } else if (head.classList.contains('coll-head--vibe')) {
      head.classList.remove('coll-head--vibe');
      head.style.removeProperty('--vibe-wide');
      head.style.removeProperty('--vibe-tall');
    }
  }

  function reflectActive() {
    const on = [];
    if (collLabel) on.push({ f: 'collection', v: '', label: collLabel });
    if (state.q.trim()) on.push({ f: 'q', v: '', label: '“' + state.q.trim() + '”' });
    CF_FACETS.forEach((f) => {
      state[f].forEach((v) => on.push({ f: f, v: v, label: labels[f + '/' + v] || v.replace(/-/g, ' ') }));
    });
    if (state.pmin != null || state.pmax != null) {
      const lo = state.pmin != null ? state.pmin : bMin, hi = state.pmax != null ? state.pmax : bMax;
      on.push({ f: 'price', v: '', label: cfMoney(lo) + ' - ' + cfMoney(hi) });
    }
    /* The count is what "Clear all" can actually clear, so the shelf pill is
       NOT in it: a badge reading 1 over a page nobody has filtered would be
       the toolbar claiming a refinement the shopper did not make, and Clear
       all cannot take a collection off anyway. The pill is still drawn - it is
       in `on` - it simply does not count as one. */
    const count = on.length - (collLabel ? 1 : 0);
    activeBoxes.forEach((box) => {
      box.replaceChildren();
      on.forEach((it) => {
        const pill = el('button', 'chip cx-pill'); pill.type = 'button';
        pill.dataset.remove = it.f; if (it.v) pill.dataset.val = it.v;
        pill.append(el('span', null, it.label));
        pill.append(el('span', 'cx-pill__x', '×'));
        pill.setAttribute('aria-label', 'Remove ' + it.label);
        box.appendChild(pill);
      });
      /* Only past two: with one pill on screen its own × IS the clear. */
      if (count > 1) {
        const all = el('button', 'chip cx-pill cx-pill--clear', 'Clear all');
        all.type = 'button'; all.setAttribute('data-cfilter-clear', '');
        box.appendChild(all);
      }
      /* `on.length`, not `count` - the shelf pill is the one thing that can be
         alone in this box, and hiding the box on a count of zero would hide
         the only way out of the collection. */
      box.hidden = on.length === 0;
    });
    const any = count > 0;
    /* one class for "something is on", read by the toolbar chip */
    root.classList.toggle('is-filtered', any);
    /* ...and the same fact ON the chip, because the chip does not always live
       inside .cx any more: past the fold the whole toolbar moves into the site
       header (initShelfHead), where an ancestor selector cannot reach it. State
       that has to travel belongs on the element that travels. */
    toggles.forEach((t) => t.classList.toggle('is-filtered', any));
    clearBtns.forEach((b) => { b.hidden = !any; });
    activeBadges.forEach((b) => { b.hidden = count === 0; b.textContent = count ? String(count) : ''; });

    /* Every group is closed by default, so each head has to carry its own
       count - otherwise a shopper who set three notes and scrolled away has
       no way to see it without opening all six again. */
    live('[data-cf-group]').forEach((g) => { const b = g.querySelector('[data-cf-group-n]'); if (b) b.textContent = groupCount(g) || ''; });
  }

  function groupCount(g) {
    const f = g.dataset.cfGroup;
    if (f === 'price') return (state.pmin != null || state.pmax != null) ? 1 : 0;
    return state[f] ? state[f].size : 0;
  }
  function anyFilter() {
    if (state.q.trim()) return true;
    if (state.pmin != null || state.pmax != null) return true;
    return CF_FACETS.some((f) => state[f].size > 0);
  }

  /* ---- The way out of a narrow shelf ----
     A vibe collection is six to twelve of the forty-one scents we stock, so
     a shopper can browse one out in a minute and meet a wall. Three things
     answer that, and all three are driven from here so they can never
     disagree with each other or with the grid:

       the bar count grows an "of 41" whenever the grid is not the house
       the panel under the last poster retitles itself once a refinement has
         cut the shelf down to a handful - it stops introducing the range and
         starts offering a rescue
       every carry link gets the live query string, so "keep my refinement"
         re-runs exactly this refinement against the whole catalogue instead
         of dumping the shopper on an unfiltered page

     The copy itself is Liquid's (data-more-*); this only chooses which of it
     applies. */
  const HOUSE = ofBox ? (parseInt(ofBox.dataset.cfOf, 10) || 0) : 0;

  /* ---- the way out that is a person ----
     (owner, 2026-08-21: "we also want a call to action AI to initiate a chat
     with jennie tho".)

     The CTA itself is pure markup - data-chat-open is bound at document level
     by initJennie, and the <a> has a real href behind it, so this adds no
     handler and breaks nothing if the module never loads. All this does is
     keep the opening line HONEST: a shopper who has already said Woody and
     evening should not have to say it again to a chat window. The words come
     from data-ask-base in Liquid and the facet labels the pills already use,
     so nothing here invents copy. */
  const askBtn = qs('[data-cf-ask]');
  const ASK_BASE = askBtn ? (askBtn.dataset.askBase || '') : '';
  function paintAsk() {
    if (!askBtn) return;
    const said = [];
    if (state.q.trim()) said.push('"' + state.q.trim() + '"');
    CF_FACETS.forEach((f) => state[f].forEach((v) => {
      const l = labels[f + '/' + v];
      if (l) said.push(l);
    }));
    askBtn.dataset.chatPrompt = said.length
      ? ASK_BASE + ' I have narrowed it to ' + said.join(', ') + '. What would you put me in?'
      : ASK_BASE + ' Can you help me narrow it down?';
  }

  function wayOut(n) {
    const on = anyFilter();
    if (ofBox) ofBox.hidden = !(HOUSE && n < HOUSE);
    const s = urlParams().toString();
    carryLinks.forEach((c) => { c.a.href = c.base + (s ? '?' + s : ''); });
    paintAsk();
    if (!moreBox) return;
    /* an empty grid already has a panel of its own - two offers stacked is
       one more than the shopper needs */
    moreBox.hidden = n === 0;
    moreBox.classList.toggle('is-filtered', on);
    const narrow = on && n > 0 && n <= CF_NARROW;
    const d = moreBox.dataset;
    if (moreH) moreH.textContent = narrow ? String(d.moreHn || '').replace('{n}', n) : d.moreH;
    if (moreC) moreC.textContent = narrow ? d.moreCn : d.moreC;
  }

  /* ---- URL <-> state ----
     One builder, two consumers: the address bar (so any view is shareable
     and Back leaves the shop) and the carry links, which hand the identical
     query string to the full collection. Every collection page reads this
     contract, so a refinement survives the move from a nine-scent shelf to
     the whole house without the shopper re-entering any of it. */
  function urlParams() {
    const p = new URLSearchParams();
    if (state.q.trim()) p.set('q', state.q.trim());
    CF_FACETS.forEach((f) => { if (state[f].size) p.set(f, Array.from(state[f]).join(',')); });
    if (state.pmin != null) p.set('pmin', Math.round(state.pmin / 100));
    if (state.pmax != null) p.set('pmax', Math.round(state.pmax / 100));
    if (state.sort && state.sort !== sortOpen) p.set('sort', state.sort);
    return p;
  }
  function syncUrl() {
    const s = urlParams().toString();
    history.replaceState(null, '', location.pathname + (s ? '?' + s : ''));
  }
  function seed(f, val) { if (!val) return; String(val).split(',').forEach((v) => { const k = cfKey(v); if (k) state[f].add(k); }); }
  function hydrate() {
    const p = new URLSearchParams(location.search);
    seed('preference', p.get('preference') || p.get('type'));   /* legacy ?type= */
    seed('size', p.get('size'));
    seed('season', p.get('season'));
    seed('occasion', p.get('occasion') || p.get('time'));
    seed('family', p.get('family'));
    seed('notes', p.get('notes'));                              /* legacy ?notes= */
    const q = p.get('q'); if (q) { state.q = q; if (searchIn) searchIn.value = q; }
    /* BEFORE the price is read: bMin/bMax are the bounds the clamp below works
       against, and a ?size= on the same link has just changed which money they
       are in. `keep` because a shared link carrying both meant both. */
    setMoney(shownSize(), true);
    const lo = p.get('pmin'), hi = p.get('pmax');
    if (lo != null && lo !== '') state.pmin = Math.max(bMin, parseInt(lo, 10) * 100);
    if (hi != null && hi !== '') state.pmax = Math.min(bMax || parseInt(hi, 10) * 100, parseInt(hi, 10) * 100);
    /* No ?sort= means the shelf's own opening sort, which is the select's
       rendered value - NOT a constant in here. Getting that wrong on Top Rated
       would have the engine's first pass re-order a wall Liquid already painted
       in rating order, which is the visible reshuffle §2c exists to avoid. */
    /* And a sort this shelf does not offer is treated as no sort at all rather
       than written into the select, which would blank its value and leave the
       trigger labelled with whatever option happens to be first. */
    const known = sortSel ? Array.from(sortSel.options).map((o) => o.value) : [];
    const s = sortAlias(p.get('sort') || '');
    if (s && known.indexOf(s) !== -1) { state.sort = s; if (sortSel) sortSel.value = s; }
    else state.sort = sortOpen;
  }

  /* ---- events ----
     DELEGATED AT DOCUMENT LEVEL, not on the section. Past the fold the whole
     toolbar - the rail, with every chip and note in it - is moved into the site
     header (initShelfHead), which is outside .cx entirely; a listener bound to
     .cx stops hearing its own controls the moment the shopper scrolls, and the
     panel goes dead while looking perfectly alive. `mine` keeps exactly the
     containment rule the root listener had, said about both addresses: this
     section's controls, wherever they happen to be standing. (rail is declared
     further down and read at click time, never at bind time.) */
  const mine = (el) => !!el && (root.contains(el) || (rail && rail.contains(el)));
  document.addEventListener('click', (e) => {
    const clr = e.target.closest('[data-cfilter-clear]');
    if (clr && mine(clr)) { clearAll(); return; }
    const more = e.target.closest('[data-cf-note-more]');
    if (more && mine(more)) { const tier = more.closest('.cf-tier'); if (tier) tier.dataset.cfOpen = '1'; paintNotes(); return; }
    /* price band chips: single-select (click the active one to clear) */
    const band = e.target.closest('[data-cf-band]');
    if (band && mine(band)) {
      const lo = parseInt(band.dataset.lo, 10), hi = parseInt(band.dataset.hi, 10);
      if (band.getAttribute('aria-pressed') === 'true') { state.pmin = state.pmax = null; }
      else { state.pmin = lo <= bMin ? null : lo; state.pmax = hi >= bMax ? null : hi; }
      apply(); return;
    }
    const chip = e.target.closest('[data-facet][data-val]');
    if (chip && mine(chip)) {
      const f = chip.dataset.facet, v = chip.dataset.val;
      state[f].has(v) ? state[f].delete(v) : state[f].add(v);
      apply(); return;
    }
    const pill = e.target.closest('[data-remove]');
    if (pill && mine(pill)) {
      const f = pill.dataset.remove;
      /* THE SHELF PILL IS A DOOR, NOT A SWITCH. There is no client-side state
         for "which collection" - the products in the wall are the ones the
         server rendered - so taking it off means going to the shelf that holds
         everything. The rest of the refinement rides along in the URL that
         syncUrl has been maintaining all along, so somebody who narrowed to
         two notes inside Midnight Allure keeps those two notes and gets the
         whole catalogue under them, which is what "switches back to all
         products" has to mean if it is not going to throw work away. */
      if (f === 'collection') {
        const here = new URL(location.href);
        const out = new URL(collOut, location.origin);
        here.searchParams.forEach((v, k) => out.searchParams.set(k, v));
        location.href = out.pathname + (out.search || '');
        return;
      }
      if (f === 'price') { state.pmin = state.pmax = null; }
      else if (f === 'q') { state.q = ''; if (searchIn) searchIn.value = ''; }
      else state[f].delete(pill.dataset.val);
      apply(); return;
    }
  });

  /* Delegated, NOT bound to the buttons that exist at init: reflectActive
     builds a Clear all pill on the fly and it has to work the same way. */
  function clearAll() {
    CF_FACETS.forEach((f) => state[f].clear());
    state.pmin = state.pmax = null; state.q = '';
    if (searchIn) searchIn.value = '';
    apply();
  }

  if (searchIn) {
    let t = 0;
    searchIn.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => { state.q = searchIn.value; apply(); }, 120); });
    /* keep the rail search as an enhancement: block the native /search submit */
    const form = searchIn.closest('form');
    if (form) form.addEventListener('submit', (e) => { if (searchIn.value.trim()) e.preventDefault(); });
  }
  if (searchClear) searchClear.addEventListener('click', () => { state.q = ''; if (searchIn) { searchIn.value = ''; searchIn.focus(); } apply(); });
  if (sortSel) sortSel.addEventListener('change', () => { state.sort = sortSel.value; apply(); });

  /* ---- the note pyramid ----
     THE TIERS OPEN SHORT. The pyramid used to scroll inside a 300px frame of
     its own, which on a phone is a second scroll surface under the same thumb;
     taking that frame away (one sheet, one scroll) left the honest measurement:
     121 notes is 5,000px of list. Neither is a shop.

     So each tier shows NOTE_CAP and offers the rest. The Find field above is
     the real answer to a specific note - typing lifts the cap and searches the
     whole pyramid - and a tier that already holds something the shopper chose
     opens in full, so a shared ?notes= link never hides its own reason.

     One painter for all of it: the cap, the find and the empty line are three
     views of the same question, and splitting them is how they drift. */
  const NOTE_CAP = 10;
  const noteFind = qs('[data-cf-note-search]');
  const noteEmpty = qs('[data-cf-note-empty]');
  function paintNotes() {
    const term = noteFind ? srchNorm(noteFind.value) : '';
    let any = false;
    live('.cf-tier').forEach((tier) => {
      const rows = Array.from(tier.querySelectorAll('.cf-note'));
      const full = !!term || tier.dataset.cfOpen === '1' || rows.some((n) => state.notes.has(n.dataset.val));
      let shown = 0;
      rows.forEach((n) => {
        const hit = !term || (n.dataset.searchText || '').indexOf(term) !== -1;
        n.hidden = !hit || (!full && shown >= NOTE_CAP);
        if (hit) any = true;
        if (!n.hidden) shown++;
      });
      const more = tier.querySelector('[data-cf-note-more]');
      if (more) {
        const rest = rows.length - shown;
        more.hidden = full || rest <= 0;
        more.textContent = 'Show all ' + rows.length;
      }
      tier.hidden = !tier.querySelector('.cf-note:not([hidden])');
    });
    if (noteEmpty) noteEmpty.hidden = any;
  }
  if (noteFind) noteFind.addEventListener('input', paintNotes);

  /* ---- DESKTOP: THE GROUPS ARE MENUS ----
     (owner, 2026-08-22: "the desktop filtering is very unintuitive".)

     From 1024px up each <details> is a pill trigger whose body hangs under it
     as a popover rather than growing the panel (the shape is components.css's
     half of this). Two rules turn six independent disclosures into a menu bar:

       ONE AT A TIME. Two popovers at this width overlap each other, and the
       row is one line tall precisely so the other five axes never move.

       IT SHUTS WHEN YOU LEAVE IT. A click anywhere outside a group closes the
       open menu, including on the grid behind it - which is the natural "done
       here" gesture on a page that has been answering live the whole time. A
       click INSIDE keeps it open: facets are multi-select, and a shopper
       picking two notes should not have to reopen the menu between them.

     Below 1024 every line of this is inert. The sheet keeps its accordion,
     where several open groups are a list rather than a pile. */
  const cfWide = window.matchMedia('(min-width: 1024px)');
  const menuMode = () => cfWide.matches;
  const groupEls = qsa('[data-cf-group]');
  const menuIsOpen = () => groupEls.some((g) => g.open);
  function closeMenus(except) {
    groupEls.forEach((g) => { if (g !== except && g.open) g.open = false; });
  }
  /* Measured, not guessed. Read on open - the toggle event fires with the
     popover already laid out - and the class is cleared first, so a menu can
     never stay flipped for a width that no longer needs it. */
  function placeMenu(g) {
    const body = g.querySelector('.cf-group__body');
    if (!body) return;
    g.classList.remove('is-flipped');
    const box = body.getBoundingClientRect();
    /* how much screen there is under this menu, in px, for the CSS to cap
       itself with. The trigger row sits at a different height on every shelf
       (a collection with a description pushes it down) and every viewport, so
       this cannot be a constant in the stylesheet. */
    g.style.setProperty('--cf-menu-h', Math.max(240, Math.round(window.innerHeight - box.top - 16)) + 'px');
    if (box.right > window.innerWidth - 12) g.classList.add('is-flipped');
  }
  groupEls.forEach((g) => g.addEventListener('toggle', () => {
    if (!g.open || !menuMode()) return;
    closeMenus(g);
    placeMenu(g);
  }));
  document.addEventListener('click', (e) => {
    if (!menuMode() || !menuIsOpen()) return;
    /* The presses wearSize makes on the cards are this panel's own doing, not
       a shopper reaching past it - closing on them would shut the menu at the
       instant a size was chosen in it. */
    if (sizing) return;
    if (e.target.closest('[data-cf-group]')) return;
    closeMenus();
  });
  /* A phone rotated into tablet-landscape, or a window dragged wide, arrives
     at 1024 with whatever the accordion had open - which at this width is a
     stack of popovers on top of each other. */
  cfWide.addEventListener('change', (e) => { if (e.matches) closeMenus(); });
  /* A drag that ends with a menu still open leaves --cf-menu-h measured for a
     window that is no longer there. Same settle-then-measure the rest of the
     theme uses, so it costs one read per drag rather than one per frame. */
  onResizeSettled(() => {
    const open = groupEls.find((g) => g.open);
    if (open && menuMode()) placeMenu(open);
  });

  /* ---- THE FILTER SURFACE - one state, both widths ----
     (owner, 2026-08-21: "always open the page filter closed on default tho".)

     There used to be two of these. This one opened the phone sheet; a second
     control collapsed the desktop rail column, remembered itself in
     localStorage, and defaulted to OPEN on any monitor 1440px or wider - which
     is the thing the owner was looking at. The column is gone (the facets now
     drop out of the docked toolbar, see components.css), so there is one
     surface, one class, one control, and nothing anywhere opens it at init.

     Deliberately NOT remembered. A remembered "open" would put a panel over
     the first row of posters on every load, which is the instruction read
     backwards. Closed is the state a collection page starts in, every time.

     Same document-scoped query as activeBadges: a trigger may live outside
     this section. */
  const toggles = Array.from(document.querySelectorAll('[data-cfilter-toggle]'));
  const rail = qs('[data-cfilter-rail]');
  const markToggles = (open) => toggles.forEach((t) => t.setAttribute('aria-expanded', open ? 'true' : 'false'));
  let openedBy = null;
  /* The sheet owns the screen on a phone, so the document behind it must not
     scroll. The desktop panel does not: it is a drop panel inside the page,
     the grid beside and under it is still live, and locking the document there
     would freeze the page a shopper is filtering. Read at the moment of the
     tap rather than cached, so a resize between opens cannot leave the lock
     on. */
  const sheetMode = () => !window.matchMedia('(min-width: 1024px)').matches;
  /* THE KEYBOARD DOES NOT COME UP WITH THE SHEET. This used to focus the
     search field on open, so every shopper who tapped Filters got a keyboard
     over the facets they came to tap, whether or not they meant to type. Focus
     lands on the surface itself instead: a screen reader is told where it now
     is, the thumb gets an unobstructed list, and the field is still one tap
     away for anyone who did want to type. preventScroll because the desktop
     panel is in the page, and focusing it would otherwise yank the wall. */
  function openSheet(from) {
    openedBy = from || null;
    root.classList.add('is-open');
    syncInRoom();
    if (rail) rail.classList.add('is-on');
    if (sheetMode()) document.documentElement.style.overflow = 'hidden';
    markToggles(true);
    if (rail) requestAnimationFrame(() => rail.focus({ preventScroll: true }));
  }
  function closeSheet() {
    root.classList.remove('is-open'); syncInRoom(); document.documentElement.style.overflow = ''; markToggles(false);
    if (rail) rail.classList.remove('is-on');
    /* the panel reopens as a clean row of shut triggers, never with last
       session's menu hanging over the posters. Desktop only: in the sheet an
       open group is a list the shopper scrolled to, and collapsing it behind
       them when they close the sheet loses their place in the pyramid. */
    if (menuMode()) closeMenus();
    /* back to the control that opened it, not to the top of the document */
    if (openedBy && document.contains(openedBy)) openedBy.focus();
    openedBy = null;
  }
  toggles.forEach((t) => t.addEventListener('click', () => { root.classList.contains('is-open') ? closeSheet() : openSheet(t); }));
  /* The toolbar changes address mid-scroll: past the fold it lives in the site
     header and the panel hangs off the header instead of off a strip in the
     page (initShelfHead). Re-anchoring an OPEN panel under the shopper while
     they are reading is worse than closing it, so the move closes it. openedBy
     is cleared first on purpose - nobody pressed anything, and yanking focus
     back to a button because the page scrolled would be its own bug. */
  document.addEventListener('juce:cf-dock-move', () => {
    if (!root.classList.contains('is-open')) return;
    openedBy = null;
    closeSheet();
  });
  qsa('[data-cfilter-close], [data-cfilter-apply]').forEach((b) => b.addEventListener('click', closeSheet));
  /* Escape unwinds ONE layer at a time: an open facet menu is the innermost
     thing the shopper is in, and taking the whole panel away underneath it
     would throw away the surface they are working in to close a dropdown. */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (menuMode() && menuIsOpen()) {
      const open = groupEls.find((g) => g.open);
      closeMenus();
      /* and it does not strand focus on <body>. It goes back to the trigger the
         menu came out of, the same way closing the panel returns focus to the
         control that opened it. */
      if (open) { const h = open.querySelector('summary'); if (h) h.focus(); }
      return;
    }
    if (root.classList.contains('is-open')) closeSheet();
  });

  /* ---- phone/tablet: the layout switcher, remembered per browser ----
     The section server-renders data-view="single" so the first paint is
     already the roomy one-up read; this only restores a different choice. */
  const viewBtns = qsa('[data-cf-view]');
  const VIEW_KEY = CF_VIEW_KEY;   /* shared with initShotMode - see the note there */
  const VIEWS = ['single', 'duo', 'list'];
  function setView(v) {
    if (VIEWS.indexOf(v) === -1) v = 'single';
    root.dataset.view = v;
    viewBtns.forEach((b) => b.setAttribute('aria-pressed', b.dataset.cfView === v ? 'true' : 'false'));
  }

  /* ---- THE SWITCH KEEPS YOUR PLACE (owner, 2026-08-28) ----
     "On mobile when swaping between different views to keep scroll index 1,
     2colm and list."

     Changing the view changes the height of every row above the fold, so
     twenty cards into the wall a tap on `list` used to leave the shopper
     looking at a completely different part of the catalogue - usually much
     further down, because list rows are shorter than posters and the same
     scrollTop then covers three times as many products. Nothing was lost, but
     the place they were reading was, and finding it again meant scrolling.

     WHAT IS HELD IS A CARD, NOT A NUMBER. A pixel offset means nothing across
     two layouts; the product under the shopper's eye means the same thing in
     all three. So: find the first card whose bottom is still below the
     anchor line, remember how far its top sits from that line, switch, and
     put it back exactly there.

     THE ANCHOR LINE IS THE STICKY BAR'S FOOT, not the top of the window -
     --head-h is measured (initHeadHeight) and the toolbar sits under it, so
     the topmost card a shopper can actually SEE is the first one below both.
     Anchoring on the viewport top would choose a card hidden behind the
     chrome and leave the visible one somewhere else.

     NO rAF. Reading offsetTop straight after writing the attribute forces the
     style and layout the browser was going to do anyway, so the new position
     is already true on the next line - and a frame callback here would let
     one painted frame of the wrong scroll through, which is the flicker this
     is meant to remove. Measured against the DOCUMENT (offsetTop + pageYOffset
     is not needed; getBoundingClientRect is relative to the viewport and the
     scroll has not moved between the two reads).

     `instant`, deliberately: this is a correction, not a journey. Smoothing it
     would animate the page away from where the shopper already is. */
  function headFoot() {
    const h = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--head-h'));
    return (Number.isFinite(h) ? h : 0) + 8;
  }
  function keepPlace(swap) {
    const line = headFoot();
    /* The cards still in the flow. A hidden one (filtered out) has no box and
       is skipped by the height test below rather than by a class check, so
       this cannot fall out of step with however the filter hides things. */
    const cards = qsa('[data-card]');
    let held = null, was = 0;
    for (let i = 0; i < cards.length; i++) {
      const r = cards[i].getBoundingClientRect();
      if (!r.height) continue;
      if (r.bottom > line) { held = cards[i]; was = r.top - line; break; }
    }
    swap();
    if (!held) return;
    const now = held.getBoundingClientRect().top - line;
    const by = now - was;
    if (!by) return;
    window.scrollBy({ top: by, left: 0, behavior: 'instant' });
  }

  if (viewBtns.length) {
    let startView = '';
    try { startView = localStorage.getItem(VIEW_KEY) || ''; } catch (e) { /* private mode */ }
    /* The restore on load is NOT wrapped: the page has not been scrolled yet,
       so there is no place to keep, and holding one would fight the browser's
       own scroll restoration on a back navigation. */
    if (startView) setView(startView);
    viewBtns.forEach((b) => b.addEventListener('click', () => {
      keepPlace(() => setView(b.dataset.cfView));
      try { localStorage.setItem(VIEW_KEY, b.dataset.cfView); } catch (e) { /* ignore */ }
    }));
  }

  /* ---- REMOVED 2026-08-21: the desktop rail toggle ----
     `is-rail-hidden`, RAIL_KEY 'juce:cf-rail-hidden:v1' and RAIL_AUTO_MIN 1440
     used to live here. They collapsed the left filter column and remembered
     the answer, defaulting to OPEN on a 1440px-or-wider monitor - which is why
     a collection page could arrive with its filter already showing, and why
     anyone who had once pressed Show filters got it open forever after.

     Both the column and the density trade it bought are gone (the facets drop
     out of the docked toolbar now, and the wall is full width at every desktop
     size), so there is nothing left to toggle and nothing to remember: the
     surface is closed on load, at every width, every time. The stale
     localStorage key is simply never read again - it is one string in a
     browser, and clearing it would mean writing to storage on every visit to
     tidy something invisible.

     The one control left is [data-cfilter-toggle] up in the filter-surface
     block above. */

  /* ---- go ---- */
  hydrate();
  /* A shared link arrives with facets already chosen. In the sheet the groups
     are a list, so opening the ones the URL turned on shows the shopper why
     the grid is short. On a desktop they are menus - opening three would be
     three popovers stacked on each other - and the badge each shut trigger
     carries does that job there instead. */
  if (!menuMode()) groupEls.forEach((g) => { if (groupCount(g)) g.open = true; });
  apply(false);
}

/* ---- The catalogue toolbar rides the nav past the fold (>=1024px) ------
   (owner, 2026-08-22: "for desktop we want to use the nav and not make that
   sticky so JUCE logo gets replaced with the filters and featured and the rest
   of the menu stuff still remains ... so that there is more viewing space".)

   Two pinned strips, one under the other, were spending 125px of every desktop
   screen before the first poster: the header at 64 and the catalogue dock at
   61 under it. The dock stops being sticky (components.css) and the header -
   which is pinned anyway, and is holding the navigation somebody reaches for
   mid-scroll - takes the controls into the slot the wordmark was using.

   IT MOVES THE REAL TOOLBAR. A cloned Filters chip and a second sort <select>
   would be two more things to hold in step with the engine, and this page has
   taught repeatedly that a mirrored control drifts. The button in the nav IS
   the button from the toolbar: same listener, same aria-expanded, same badge,
   same panel hanging off it. What the move costs is the ancestor selectors -
   .cx:not(.is-open) .cx-rail cannot reach a rail that is no longer inside .cx -
   which is why the engine now writes those two states onto the rail and the
   chip themselves.

   THE GHOST IS NOT OPTIONAL. The dock is above the viewport by the time it
   leaves, so taking its 61px out of the flow drops everything below it by 61px
   while the shopper is reading. A placeholder of the measured height holds the
   gap until the dock comes home.

   THE SENTINEL is what the observer watches, never the dock: the dock ends up
   INSIDE the pinned header, where it would report itself permanently on screen
   and flip the state back on the very next frame, forever. */
function initShelfHead() {
  const dock = document.querySelector('.cx-dock');
  const shelf = document.querySelector('[data-head-shelf]');
  const head = document.querySelector('.site-head');
  const cx = document.querySelector('[data-cfilter]');
  if (!dock || !shelf || !head || !cx || !('IntersectionObserver' in window)) return;

  const home = dock.parentNode;
  const wide = window.matchMedia('(min-width: 1024px)');

  const ghost = document.createElement('div');
  ghost.className = 'cx-dock__ghost';
  ghost.setAttribute('aria-hidden', 'true');
  const sentinel = document.createElement('div');
  sentinel.className = 'cx-dock__sentinel';
  sentinel.setAttribute('aria-hidden', 'true');
  home.insertBefore(sentinel, dock);

  let pinned = false;
  function pin(on) {
    if (on === pinned || (on && !wide.matches)) return;
    pinned = on;
    /* Told BEFORE the move, so the panel is shut and out of the flow before
       its box changes address (initCollectionFilter listens). */
    document.dispatchEvent(new CustomEvent('juce:cf-dock-move'));
    if (on) {
      /* the closed height: the panel is out of flow wherever it opens, so it
         is never part of the gap this has to hold */
      ghost.style.height = dock.offsetHeight + 'px';
      home.insertBefore(ghost, dock);
      shelf.appendChild(dock);
    } else {
      home.insertBefore(dock, ghost);
      ghost.remove();
    }
    head.classList.toggle('is-shelf', on);
    cx.classList.toggle('is-pinned', on);
  }

  /* The trip line is the measured header, never a literal: the same number
     every other pinned thing in the theme offsets by, and it is re-read when a
     resize settles because a signed-in account pill can change it. */
  let io = null;
  function watch() {
    if (io) io.disconnect();
    io = null;
    if (!wide.matches) { pin(false); return; }
    const h = Math.round(parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--head-h')) || 64);
    io = new IntersectionObserver((entries) => {
      pin(!entries[entries.length - 1].isIntersecting);
    }, { rootMargin: '-' + h + 'px 0px 0px 0px', threshold: 0 });
    io.observe(sentinel);
  }
  watch();
  wide.addEventListener('change', watch);
  onResizeSettled(watch);
}
